import React, { useEffect, useRef, useState } from "react";
import axios, { enableAlgoKey } from "../../API/axios";
import JobList from "./JobList";
import InfoSharpIcon from "@mui/icons-material/InfoSharp";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoogleAdUnit from "../../components/GoogleAdUnit";
import "./JobSearchRedesign.css";

const JobRecommendations = () => {
    const scrollContainerRef = useRef();
    const listGenerationRef = useRef(0);
    const isFetchingMoreRef = useRef(false);
    const pageRef = useRef(1);
    const totalPagesRef = useRef(1);
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const from = location.state?.from;
    const sessionId = localStorage.getItem("sessionId");
    const guestModeData = localStorage.getItem("guestMode");
    const userUid = localStorage.getItem("userUid");
    const jobRecommendationsIds = localStorage.getItem("jobRecommendationsIds");
    const ProfilePercent = localStorage.getItem("ProfilePercentage");

    const handleBack = () => {
        if (from === "newsfeed") {
            navigate("/NewsFeed");
            return;
        }
        if (from === "jobs") {
            navigate("/SearchJob");
            return;
        }
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate("/SearchJob");
    };

    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [jobsList, setJobsList] = useState([]);
    const [isError, setIsError] = useState(false);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [nextBatchLoading, setNextBatchLoading] = useState(false);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    useEffect(() => {
        totalPagesRef.current = totalPages;
    }, [totalPages]);

    useEffect(() => {
        if (!nextBatchLoading && !isLoading) {
            isFetchingMoreRef.current = false;
        }
    }, [nextBatchLoading, isLoading]);

    const normalizeRecommendationJob = (job) => ({
        ...job,
        isApplied: job.isApplied ?? job.jobActionInfo?.isApplied ?? false,
        isSaved: job.isSaved ?? job.jobActionInfo?.isSaved ?? false,
    });

    const getJobRecommendations = (pageNumber, options = {}) => {
        const { replaceList = false } = options;
        const isFirstPage = pageNumber === 1;
        const requestGeneration = listGenerationRef.current;
        const isGuest = guestModeData === "true";
        const hasRecommendationIds =
            jobRecommendationsIds &&
            jobRecommendationsIds !== "null" &&
            String(jobRecommendationsIds).trim() !== "";

        // Guests (and users) with no recommendation ids have nothing to fetch — show empty state.
        if (isFirstPage && !hasRecommendationIds) {
            setIsLoading(false);
            setNextBatchLoading(false);
            setHasMoreData(false);
            setJobsList([]);
            setIsError(true);
            return;
        }

        if (isFirstPage) {
            setIsLoading(true);
            setNextBatchLoading(false);
            if (replaceList) {
                setJobsList([]);
                setIsError(false);
            }
        } else {
            setNextBatchLoading(true);
        }

        const data = {
            deviceType: "Web",
            sessionId: sessionId,
            pageNumber: pageNumber,
            pageSize: 12,
            jobRecommendationsIds: jobRecommendationsIds,
            enableAlgo: enableAlgoKey,
        };

        axios
            .post(`/Feature/JobRecommendations`, data)
            .then((response) => {
                if (requestGeneration !== listGenerationRef.current) {
                    return;
                }

                const res = response.data.responseData;
                const apiStatus = response.data?.status;
                const newPosts = (res?.dataList || []).map(normalizeRecommendationJob);
                const isEmpty = !res || apiStatus === "Warning" || newPosts.length === 0;

                if (isEmpty && isFirstPage) {
                    setHasMoreData(false);
                    setIsError(true);
                    setJobsList([]);
                } else if (isEmpty) {
                    setHasMoreData(false);
                } else {
                    setIsError(false);
                    const responseTotalPages = res.paging?.totalPages ?? 1;

                    setTotalPages(responseTotalPages);
                    setJobsList((prev) => {
                        if (isFirstPage || replaceList) {
                            return newPosts;
                        }

                        const postSet = new Set(prev.map((p) => p.jobId));
                        const uniqueNewPosts = newPosts.filter((p) => !postSet.has(p.jobId));
                        return [...prev, ...uniqueNewPosts];
                    });

                    setHasMoreData(pageNumber < responseTotalPages);
                }

                if (isFirstPage) {
                    setIsLoading(false);
                } else {
                    setNextBatchLoading(false);
                }
            })
            .catch((error) => {
                if (requestGeneration !== listGenerationRef.current) {
                    return;
                }

                setIsError(true);
                if (isFirstPage) {
                    setJobsList([]);
                    setIsLoading(false);
                } else {
                    setNextBatchLoading(false);
                }

                if (!(error.response && error.response.status === 404)) {
                    console.log(error);
                }
            });
    };

    useEffect(() => {
        if (page > 1 && hasMoreData && page <= totalPages) {
            getJobRecommendations(page);
        }
    }, [page]);

    useEffect(() => {
        listGenerationRef.current += 1;
        isFetchingMoreRef.current = false;
        setPage(1);
        setHasMoreData(true);
        getJobRecommendations(1, { replaceList: true });
    }, [jobRecommendationsIds, sessionId]);

    const handleInfiniteScroll = () => {
        if (
            window.innerHeight + window.scrollY >= document.body.scrollHeight - 100 &&
            !nextBatchLoading &&
            !isFetchingMoreRef.current &&
            hasMoreData &&
            !isLoading &&
            pageRef.current < totalPagesRef.current
        ) {
            isFetchingMoreRef.current = true;
            setPage((prev) => {
                if (prev >= totalPagesRef.current) {
                    isFetchingMoreRef.current = false;
                    return prev;
                }
                return prev + 1;
            });
        }
    };

    
    useEffect(() => {
        window.addEventListener("scroll", handleInfiniteScroll);
        return () => window.removeEventListener("scroll", handleInfiniteScroll);
    }, [nextBatchLoading, hasMoreData, isLoading]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    return (
        <>
            <Helmet>
                <title>Job Recommendations | Welinkjobs </title>
                <meta
                    name="description"
                    content="Discover personalized job recommendations tailored to your profile on Welinkjobs."
                />
                <meta property="og:title" content="Job Recommendations | Welinkjobs" />
                <meta
                    property="og:description"
                    content="Discover personalized job recommendations tailored to your profile on Welinkjobs."
                />
                <meta property="og:url" content="https://welinkjobs.com/SearchJob/Recommendations" />
            </Helmet>
            <div className="p-lg-5 p-md-2 p-2 mt-5 PaddingTop padding-bottom wlj-job-search-page">
                
                <div className="advertiseImage">
                    <div className="advertisementview mb-2">
                        <div
                            className="advertisementContainer"
                            style={{
                                width: "100%",
                                height: "180px",
                                overflow: "hidden",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <GoogleAdUnit
                                adSlot="9135916775"
                                style={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    margin: "0 auto",
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="row jobMainContaine">
                    <div className="col-lg-3 col-md-4">
                        <div className="advertiseImage mt-3">
                            <div className="advertisementview">
                                <div
                                    className="advertisementContainer"
                                    style={{
                                        width: "100%",
                                        height: "200px",
                                        overflow: "hidden",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <GoogleAdUnit
                                        adSlot="9135916775"
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            height: "100%",
                                            margin: "0 auto",
                                        }}
                                    />
                                </div>
                                <h1 className="adText">{t("Newsfeed.Advertisement")}</h1>
                            </div>
                        </div>
                        <div className="advertiseImage mt-3">
                            <div className="advertisementview">
                                <div
                                    className="advertisementContainer"
                                    style={{
                                        width: "100%",
                                        height: "200px",
                                        overflow: "hidden",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <GoogleAdUnit
                                        adSlot="9135916775"
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            height: "100%",
                                            margin: "0 auto",
                                        }}
                                    />
                                </div>
                                <h1 className="adText">{t("Newsfeed.Advertisement")}</h1>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-9 col-md-8">
                        <div className="job-recommendations-header">
                            <button
                                type="button"
                                className="job-recommendations-back"
                                onClick={handleBack}
                            >
                                <ArrowBackSharpIcon className="job-recommendations-back-icon" />
                                <span>{t("Common.Back", { defaultValue: "Back" })}</span>
                            </button>
                            <h1 className="job-recommendations-title">
                                {t("Recommendations.Recommendations")}
                            </h1>
                        </div>
                        <JobList
                            refContainer={scrollContainerRef}
                            jobsList={jobsList}
                            setJobsList={setJobsList}
                            isLoading={isLoading}
                            isError={isError}
                            page={page}
                            getAllJobList={() => getJobRecommendations(1, { replaceList: true })}
                            totalPages={totalPages}
                            setTotalPages={setTotalPages}
                            nextBatchLoading={nextBatchLoading}
                            hasMoreData={hasMoreData}
                            hideSearchBar={true}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default JobRecommendations;
